import { Card, Text, Heading, Logo, Paragraph as P, IconButton, Icon } from '@devfolioco/genesis';
import { Analytics } from '@devfolioco/helpers';
import { Column, Row } from 'hedron';
import { connect } from 'react-redux';
import { BASE_URL, OG_BASE } from '@constants/environment';
import { Helmet } from 'react-helmet';
import Media from 'react-media';
import React from 'react';

import Loader from 'components/Loader';
import Navigation from 'components/Navigation';
import useIsMounted from '@helpers/useIsMounted';

import BlurredContentSrc from 'assets/content-blurred.jpg';
import BlurredContentMobileSrc from 'assets/content-blurred-mobile.jpg';
import { ACTION_STATUS as STATUS, ANALYTICS_TRACKING_EVENTS } from '../../constants';

import TopBar from './components/TopBar';
import ActionBar from './components/ActionBar';
import Project from './components/Project';
import Experience from './components/Experience';

import { CardContent, Section, List, DetailContainer, CardWrapper, LoggedOutHeaderDiv, StyledMarkdown } from './style';

const LoggedInHiddenUserView = React.memo(({ username }) => {
  const isMounted = useIsMounted();

  const handleSettingsClick = () => {
    Analytics.trackEvent(ANALYTICS_TRACKING_EVENTS.OPENED_SETTINGS_FROM_HIDDEN_PROFILE, {
      username,
    });
  };

  return (
    <>
      <DetailContainer>
        <Card background="blue-4" elevation={3}>
          <Text size={20} color="white-0" weight="extrabold" style={{ lineHeight: '28px' }} upgrade>
            You’ve hidden your Devfolio profile
          </Text>
          <div style={{ marginTop: '8px' }}>
            <Text size={16} color="blue-1" upgrade>
              Your profile can’t be seen by you or anyone on the internet. You can make it public in just two steps:
            </Text>
            <P color="blue-1" style={{ marginTop: '24px', fontSize: '16px', lineHeight: '28px' }}>
              1. Go to{' '}
              <a href="/settings" onClick={handleSettingsClick} style={{ color: 'white' }}>
                Settings
                {/* Render the icon only on client because it looks weird initially when rendered from server */}
                {isMounted && <Icon name="chevron-right" style={{ verticalAlign: 'text-top' }} />}
              </a>
              <br />
              2. Find and uncheck the option which says &apos;Hide My Devfolio profile from public view&apos;
            </P>
          </div>
        </Card>
        <Card elevation={3}>
          <Text
            size={12}
            color="grey-6"
            weight="bold"
            font="secondary"
            style={{ letterSpacing: '0.12em', lineHeight: '16px', textTransform: 'uppercase' }}
            upgrade
          >
            What you get with a Devfolio profile
          </Text>
          <List>
            <li>
              <IconButton icon="zap" color="white-0" size="large" background="teal-6" />
              <Text upgrade>
                Easily maintain an up-to-date resume
                <br />
                <Text upgrade>Share your social profile, experience and skills</Text>
              </Text>
            </li>
            <li>
              <IconButton icon="clipboard" color="white-0" size="large" background="teal-6" />
              <Text upgrade>
                All your projects in one place <br />
                <Text upgrade>Showcase your hackathon projects and add more</Text>
              </Text>
            </li>
            <li>
              <IconButton icon="link" color="white-0" size="large" background="teal-6" />
              <Text upgrade>
                Public URL to share your profile <br />
                <Text upgrade>
                  Your URL: <a href={`https://devfolio.co/@${username}`}>devfolio.co/@{username}</a>
                </Text>
              </Text>
            </li>
          </List>
        </Card>
      </DetailContainer>
    </>
  );
});

const DefaultHiddenProfileView = ({ deviceType }) => (
  <Media query="(max-width: 768px)" defaultMatches={deviceType === 'mobile' || deviceType === 'smartphone'}>
    {matches => (
      <img
        src={matches ? BlurredContentMobileSrc : BlurredContentSrc}
        alt="Hidden profile content"
        style={{ width: '100%' }}
      />
    )}
  </Media>
);

class MyProfile extends React.Component {
  componentDidMount() {
    const { status } = this.props;

    if (status !== STATUS.SUCCESS) {
      if (typeof window === 'object') {
        window.location.href = `${BASE_URL}404`;
      }
    }
  }

  render() {
    const {
      bio,
      experiences,
      expertise,
      firstName,
      isLoggedIn,
      lastName,
      match: { params },
      otherProfiles,
      profileImageURL,
      projects,
      skills,
      myUsername,
      status,
      publicProfileVisible,
      isUserSelf,
      device,
    } = this.props;

    const { username } = params;
    const displayName = (firstName && lastName && `${firstName} ${lastName}`) || username;

    const title = displayName || 'Profile';
    const ogImage = profileImageURL || `${OG_BASE}${encodeURIComponent(`**${title}**.png?md=1&fontSize=110px`)}`;
    const description = (bio && bio.slice(0, 100)) || '';
    const url = `${BASE_URL}@${username}`;

    // when the user is logged in and browsing other user's private profile
    const isOtherPrivateProfile = isLoggedIn && !isUserSelf && !publicProfileVisible;

    // when the user is not logged in and browsing a private profile
    const isLoggedOutPrivateProfile = !isLoggedIn && !publicProfileVisible;

    if (status !== STATUS.SUCCESS) {
      return <Loader />;
    }

    const handleCreateDevfolioClick = () => {
      Analytics.trackEvent(ANALYTICS_TRACKING_EVENTS.CLICKED_CREATE_YOUR_DEVFOLIO);
    };

    return (
      <>
        <Helmet>
          <title>{title}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="description" content={description} />
          <meta property="og:site_name" content="Devfolio" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={url} />
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={ogImage} />
          <meta property="og:locale" content="en_US" />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@devfolio" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:url" content={url} />
          <meta name="twitter:image" content={ogImage} />
        </Helmet>
        {isLoggedIn && <Navigation />}

        <Row>
          <Column>
            <CardWrapper>
              {!isLoggedIn && (
                <LoggedOutHeaderDiv>
                  <a href={`${BASE_URL}discover`} aria-label="Devfolio Discover">
                    <Logo />
                  </a>

                  <a href="/signup" onClick={handleCreateDevfolioClick}>
                    Create your own Devfolio?
                  </a>
                </LoggedOutHeaderDiv>
              )}

              <Card style={{ position: 'relative', width: '100%' }} elevation={3} noPadding>
                {publicProfileVisible && <ActionBar username={username} myUsername={myUsername} />}
                <TopBar
                  expertise={expertise}
                  otherProfiles={otherProfiles}
                  profileImageURL={profileImageURL}
                  displayName={displayName}
                  publicProfileVisible={publicProfileVisible}
                  isUserSelf={isUserSelf}
                  username={username}
                />
                {/* View when the user is logged in and browsing other user's private profile Or General view when someone is browsing a private profile and not logged in */}
                {(isOtherPrivateProfile || isLoggedOutPrivateProfile) && (
                  <DefaultHiddenProfileView deviceType={device?.type ?? ''} />
                )}

                {/* Default view when the profile is visible */}
                {publicProfileVisible && (
                  <CardContent>
                    <StyledMarkdown
                      // Only allowed elements that are allowed in GFM specs
                      allowedElements={[
                        'h1',
                        'h2',
                        'h3',
                        'h4',
                        'h5',
                        'h6',
                        'p',
                        'a',
                        'img',
                        'ul',
                        'ol',
                        'li',
                        'pre',
                        'code',
                        'hr',
                        'em',
                        'strong',
                        'table',
                        'thead',
                        'tbody',
                        'tr',
                        'th',
                        'td',
                        'br',
                        'del',
                      ]}
                    >
                      {bio}
                    </StyledMarkdown>

                    <Section>
                      {Array.isArray(projects) && projects.length > 0 && (
                        <div>
                          <Heading appearance="H4" color="grey-7">
                            Projects
                          </Heading>

                          {projects.map(project => (
                            <Project key={project.slug} {...project} />
                          ))}
                        </div>
                      )}

                      {Array.isArray(skills) && skills.length > 0 && (
                        <div>
                          <Heading appearance="H4" color="grey-7">
                            Skills
                          </Heading>

                          {skills.map((skill, i) => (
                            <div key={i}>
                              <Text>{skill.name}</Text>
                            </div>
                          ))}
                        </div>
                      )}
                    </Section>

                    <Section>
                      {Array.isArray(experiences) && experiences.length > 0 && (
                        <div>
                          <Heading appearance="H4" color="grey-7">
                            Experience
                          </Heading>

                          <ul style={{ listStyle: 'none', paddingLeft: 0 }}>
                            {experiences.map((experience, i) => (
                              <li key={i} style={{ marginBottom: 32 }}>
                                <Experience {...experience} />
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Section>
                  </CardContent>
                )}
              </Card>
              {/*  View when user is logged in and visiting their private profile (aka hidden profile) */}
              {isLoggedIn && isUserSelf && !publicProfileVisible && <LoggedInHiddenUserView username={username} />}
            </CardWrapper>
          </Column>
        </Row>
      </>
    );
  }
}

const mapStateToProps = ({ profile, authentication }) => ({
  bio: profile.bio,
  experiences: profile.experiences,
  expertise: profile.expertise,
  firstName: profile.firstName,
  hackathons: profile.hackathons,
  isLoggedIn: authentication.isAuthenticated,
  lastName: profile.lastName,
  otherProfiles: profile.otherProfiles,
  profileImageURL: profile.profileImageURL,
  publicProfileVisible: profile.publicProfileVisible,
  projects: profile.projects,
  skills: profile.skills,
  status: profile.status.fetchUserInfo,
  myUsername: authentication.username,
  isUserSelf: profile.isUserSelf,
  device: profile.device,
});

export default connect(mapStateToProps)(MyProfile);
